import React, { useState, useEffect, useMemo } from "react";
import {
  DatePicker,
  Table,
  Popover,
  Card,
  Button,
  Row,
  Col,
  Avatar,
  Typography,
  Divider,
  Space,
  Modal,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import {
  PlusOutlined,
  SettingOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  getAppointmentDetailsByApptId,
  deleteAppointmentsByApptId,
} from "../Clients/Appointments/AppointmentApi";
import {
  getAppointmentsByDate,
  getAppointmentCountByStatusId,
  deleteTechnicianLeaveById,
} from "./DispatchApi";
import { getTechniciansAPI } from "CommonComponents/Lookup/LookupAPI";
import StatusEnum from "features/Enums/StatusEnum";
import SelectClient from "./SelectClient";
import { use } from "react";
import DispatchTool from "features/Clients/Appointments/DispatchTool";
import { GetSubContractorsApi } from "features/Admin/SubContractor/SubContractorAPI";

const timeSlots = Array.from(
  { length: 24 },
  (_, i) => `${i.toString().padStart(2, "0")}:00`
);

const { Text } = Typography;

const statusColors = {
  [StatusEnum.NotStarted.id]: "#a7a7a7",
  [StatusEnum.InProgress.id]: "#f57f01",
  [StatusEnum.Started.id]: "#f57f01",
  [StatusEnum.Paused.id]: "#f57f01",
  [StatusEnum.Resumed.id]: "#f57f01",
  [StatusEnum.Completed.id]: "#3cb043",
};

function Schedule() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const technicians = useSelector((state) => state.lookup.technicians);
  const appointments = useSelector(
    (state) => state.dispatch.appointmentsByDateList?.data
  );
  const flatAppointments = appointments?.flatMap(
    (slot) => slot.appointments || []
  );
  const appointmentCount = useSelector(
    (state) => state.dispatch.appointmentCount
  );
  const appointmentByApptId = useSelector(
    (state) => state.appointments.appointmentByApptId
  );
  const subContractorsList = useSelector(
    (state) => state.subContractor.subContractorRecords
  );
  const [date, setDate] = useState(dayjs());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialValues, setModalInitialValues] = useState(null); // <-- add this
  const [modalMode, setModalMode] = useState("book");
  const [showClientPartnerModal, setShowClientPartnerModal] = useState(false);
  const [pendingBookMode, setPendingBookMode] = useState("book");
  const [pendingInitialValues, setPendingInitialValues] = useState(null); // "book" or "reschedule"
  const [selectedTechId, setSelectedTechId] = useState(null);
  const [selectedSubContractorId, setSelectedSubContractorId] = useState(null);
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [selectedTime, setSelectedTime] = useState();
  const [selectClientModalOpen, setSelectClientModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // Added currentPage state
  const [editAppointment, setEditAppointment] = useState(null);

  const pagination = useMemo(
    () => ({
      pageNumber: currentPage,
      pageSize: 10,
      searchQuery: "",
      companyFilter: null,
      statusFilter: null,
      salespersonFilter: null,
      startDate: null,
      endDate: null,
      quoteTypeIds: null,
      statusIds: null,
    }),
    [currentPage]
  );

  const showModal = (resourceId, time, resourceType) => {
    setEditAppointment(null);
    //setSelectedTechId(techId);
    setSelectedTime(time);
    setSelectedResourceType(resourceType);

    if (resourceType === "technician") {
      setSelectedTechId(resourceId);
      setSelectedSubContractorId(null);
    } else if (resourceType === "subcontractor") {
      setSelectedSubContractorId(resourceId);
      setSelectedTechId(null);
    }
    setSelectClientModalOpen(true);
  };

  const handleClientPartnerSelect = (selection) => {
    setShowClientPartnerModal(false);
    setModalMode(pendingBookMode);
    setModalInitialValues({
      ...pendingInitialValues,
      ...selection,
    });
    setIsModalOpen(true);
  };

  useEffect(() => {
    dispatch(getTechniciansAPI());
    dispatch(
      GetSubContractorsApi({
        pageNumber: currentPage,
        pageSize: 100,
        searchQuery: "",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (date) {
      dispatch(
        getAppointmentsByDate({
          startDate: date.format("YYYY-MM-DD"),
          pageNo: 1,
          pageSize: 24,
        })
      );
      dispatch(getAppointmentCountByStatusId(date.format("YYYY-MM-DD")));
    }
  }, [date, dispatch]);

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditAppointment(null);
    setSelectedTechId(null);
    setSelectedSubContractorId(null);
    setSelectedResourceType(null);
  };

  const normalizeTime = (t) => t?.slice(0, 5);

  const isTimeInRange = (start, end, target) => {
    const toMinutes = (t) => {
      const [hours, minutes] = t.split(":").map(Number);
      return hours * 60 + (minutes || 0);
    };

    const startMinutes = toMinutes(start);
    const endMinutes = end === "24:00" ? 1440 : toMinutes(end);
    const targetMinutes = toMinutes(target);
    if (startMinutes > endMinutes) {
      return targetMinutes >= startMinutes || targetMinutes < endMinutes;
    } else {
      return targetMinutes >= startMinutes && targetMinutes < endMinutes;
    }
  };
  const isPastSlot = (slotTime) => {
    const now = dayjs();
    const selectedDate = date.startOf("day");

    if (selectedDate.isBefore(now.startOf("day"))) {
      return true;
    }

    if (selectedDate.isAfter(now.startOf("day"))) {
      return false;
    }

    const slotDateTime = selectedDate
      .hour(parseInt(slotTime.split(":")[0]))
      .minute(parseInt(slotTime.split(":")[1]));

    return slotDateTime.isBefore(now);
  };

  const handleCancelAppointment = (appt) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: "Appointment.DeleteConfirmation",
      }),
      content: intl.formatMessage({
        id: "Appointment.DeleteContext",
      }),
      okText: intl.formatMessage({ id: "Common.Yes" }),
      okType: "danger",
      cancelText: intl.formatMessage({ id: "Common.No" }),
      onOk: () => {
        dispatch(
          deleteAppointmentsByApptId(
            appt.id, // appointment id
            appt.workOrderId, // workorder id
            pagination,
            date.format("YYYY-MM-DD")
          )
        ).then(() => {
          dispatch(
            getAppointmentsByDate({
              startDate: date.format("YYYY-MM-DD"),
              pageNo: 1,
              pageSize: 24,
            })
          );

          dispatch(getAppointmentCountByStatusId(date.format("YYYY-MM-DD")));
        });
      },
    });
  };

  const handleCancelLeave = (leave) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: "Appointment.DeleteConfirmation",
      }),
      content: intl.formatMessage({
        id: "Appointment.DeleteContext",
      }),
      okText: intl.formatMessage({ id: "Common.Yes" }),
      okType: "danger",
      cancelText: intl.formatMessage({ id: "Common.No" }),
      onOk: () => {
        dispatch(
          deleteTechnicianLeaveById(leave.leaveId, date.format("YYYY-MM-DD"))
        ).then(() => {
          dispatch(
            getAppointmentsByDate({
              startDate: date.format("YYYY-MM-DD"),
              pageNo: 1,
              pageSize: 24,
            })
          );

          dispatch(getAppointmentCountByStatusId(date.format("YYYY-MM-DD")));
        });
      },
    });
  };

  const handleReschedule = (appt) => {
    dispatch(getAppointmentDetailsByApptId(appt.id));
    setEditAppointment(appt);
    setIsModalOpen(true);
  };
  const getAppointmentSpan = (row, time) => {
    if (!row) return { children: null, props: {} };
    const viewDate = date.format("YYYY-MM-DD");
    const isTechnician = row.type === "technician";
    const isSubcontractor = row.type === "subcontractor";

    const appt = flatAppointments?.find((a) => {
      if (isTechnician) {
        const isAssignedTech = a.appointmentTechnicians?.some(
          (at) => at.technicianId === row.id
        );
        if (!isAssignedTech && a.technicianId !== row.id) {
          return false;
        }
      }

      if (isSubcontractor) {
        const isAssignedSubc = a.appointmentSubContractors?.some(
          (asc) => asc.subContractorId === row.id
        );
        if (!isAssignedSubc && a.subContractorId !== row.id) {
          return false;
        }
      }

      if (viewDate < a.startDate || viewDate > a.endDate) return false;

      let rangeStart = "00:00";
      let rangeEnd = "24:00";

      if (viewDate === a.startDate) rangeStart = normalizeTime(a.startTime);
      if (viewDate === a.endDate) rangeEnd = normalizeTime(a.endTime);
      if (rangeEnd === "00:00" && viewDate === a.endDate) {
        rangeEnd = "24:00";
      }

      return isTimeInRange(rangeStart, rangeEnd, time);
    });

    if (!appt) {
      if (isPastSlot(time)) {
        return {
          children: (
            <div
              className="appointment-cell past-slot"
              style={{
                background: "#f0f0f0",
                color: "#999",
                textAlign: "center",
                borderRadius: 0,
                padding: "6px",
                cursor: "not-allowed",
                fontSize: 12,
                height: "48px",
                lineHeight: "38px",
              }}
            ></div>
          ),
          props: {},
        };
      }
      return {
        children: (
          <div className="appointment-cell">
            <div className="appointment-hover-btn">
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => showModal(row.id, time, row.type)}
              >
                <FormattedMessage id="Appointment.BookAppointment" />
              </Button>
            </div>
          </div>
        ),
        props: {},
      };
    }

    let apptRangeStart = "00:00";
    let apptRangeEnd = "24:00";

    if (viewDate === appt.startDate)
      apptRangeStart = normalizeTime(appt.startTime);
    if (viewDate === appt.endDate) apptRangeEnd = normalizeTime(appt.endTime);

    // const isStartCell = time === apptRangeStart;
    // if (!isStartCell) return { children: null, props: { colSpan: 0 } };

    // let colSpan;
    // if (apptRangeEnd === "24:00") {
    //   const startIdx = timeSlots.findIndex((t) => t === apptRangeStart);
    //   colSpan = timeSlots.length - startIdx;
    // } else {
    //   const startIdx = timeSlots.findIndex((t) => t === apptRangeStart);
    //   const endIdx = timeSlots.findIndex((t) => t === apptRangeEnd);
    //   colSpan = endIdx - startIdx;
    // }

    // ✅ Updated: safe handling for end-of-day appointments
    const startIdx = timeSlots.findIndex((t) => t === apptRangeStart);
    let endIdx = timeSlots.findIndex((t) => t === apptRangeEnd);

    // If end time not found in timeSlots (e.g., 23:59 or 24:00), span to last slot
    if (endIdx === -1) {
      endIdx = timeSlots.length;
    }

    // Only render start cell
    const isStartCell = time === timeSlots[startIdx];
    if (!isStartCell) return { children: null, props: { colSpan: 0 } };

    // Correct colspan
    const colSpan = endIdx - startIdx;

    const leaveStart = dayjs(
      `${appt.startDate} ${normalizeTime(appt.startTime)}`,
      "YYYY-MM-DD HH:mm"
    );

    const isPastLeave = leaveStart.isBefore(dayjs());

    if (appt.type === 2) {
      let apptRangeStart = "00:00";
      let apptRangeEnd = "24:00";

      if (viewDate === appt.startDate)
        apptRangeStart = normalizeTime(appt.startTime);
      if (viewDate === appt.endDate) apptRangeEnd = normalizeTime(appt.endTime);

      // // Find matching indexes safely
      // const startIdx = timeSlots.findIndex((t) => t === apptRangeStart);
      // const endIdx = timeSlots.findIndex((t) => t === apptRangeEnd);

      // // If not found, adjust
      // const safeStart = startIdx !== -1 ? startIdx : 0;
      // const safeEnd = endIdx !== -1 ? endIdx : timeSlots.length;

      // const isStartCell = time === timeSlots[safeStart];
      // if (!isStartCell) return { children: null, props: { colSpan: 0 } };

      // const colSpan = safeEnd - safeStart;

      // ✅ Updated: leave handling safe index
      const startIdx = timeSlots.findIndex((t) => t === apptRangeStart);
      let endIdx = timeSlots.findIndex((t) => t === apptRangeEnd);

      if (endIdx === -1) endIdx = timeSlots.length;

      const isLeaveStartCell = time === timeSlots[startIdx];
      if (!isLeaveStartCell) return { children: null, props: { colSpan: 0 } };

      const colSpan = endIdx - startIdx;

      const leaveContent = (
        <>
          <div style={{ maxWidth: 300 }} className="schedule-popover">
            <Text strong style={{ fontSize: 14, color: "#d4380d" }}>
              Technician Unavailable
            </Text>
            <p className="f11">
              <strong>
                {appt.startDate} {normalizeTime(appt.startTime)} →{" "}
                {appt.endDate} {normalizeTime(appt.endTime)}
              </strong>
            </p>
            <Divider style={{ margin: "6px 0" }} />
            <Text>{appt.unavailabilityReason || "No reason provided"}</Text>
          </div>
          <div style={{ marginTop: 8, textAlign: "center" }}>
            <Button
              danger
              disabled={isPastLeave}
              onClick={() => handleCancelLeave(appt)}
            >
              <CloseCircleOutlined /> <FormattedMessage id="Common.Cancel" />
            </Button>
          </div>
        </>
      );

      return {
        children: (
          <Popover content={leaveContent}>
            <div
              style={{
                backgroundColor: "#ffccc7", // light red
                border: "none",
                borderRadius: 0,
                padding: "4px",
                textAlign: "center",
                fontWeight: 500,
                color: "#a8071a",
                height: "48px",
                lineHeight: "38px",
                fontSize: "16px",
              }}
            >
              {/* <FormattedMessage id="Dispatch.Leave" /> */}{" "}
              <FormattedMessage id="Dispatch.Leave" />
            </div>
          </Popover>
        ),
        props: { colSpan },
      };
    }

    const clientDetails = appt.clientDetails || {};
    const workLocationAddress = appt.workLocationAddress || {};
    const isActionDisabled =
      appt.appointmentStatusId === StatusEnum.InProgress.id ||
      appt.appointmentStatusId === StatusEnum.Started.id ||
      appt.appointmentStatusId === StatusEnum.Paused.id ||
      appt.appointmentStatusId === StatusEnum.Completed.id;

    const content = (
      <div style={{ maxWidth: 350 }} className="schedule-popover">
        <Space align="start" style={{ width: "100%" }}>
          <Avatar size={42} icon={<HomeOutlined />} />
          <div>
            <Text strong style={{ fontSize: 14 }}>
              {clientDetails.name ||
                clientDetails.companyName ||
                "Unknown Client"}
            </Text>
            <p className="f11">
              <strong>
                {appt.appointmentNumber} from {appt.startDate}{" "}
                {normalizeTime(appt.startTime)} to {appt.endDate}{" "}
                {normalizeTime(appt.endTime)}
              </strong>
            </p>
          </div>
        </Space>

        <div style={{ marginTop: 2 }}>
          <Space direction="vertical" size={6}>
            <Space>
              <Text>
                <PhoneOutlined style={{ marginRight: 8 }} />
                {clientDetails.phone || "N/A"}
              </Text>
              <Text style={{ marginLeft: 10 }}>
                <MailOutlined style={{ marginRight: 8 }} />
                {clientDetails.email || "N/A"}
              </Text>
            </Space>
            <Text style={{ display: "block", width: "100%" }}>
              <EnvironmentOutlined style={{ marginRight: 8 }} />
              {[
                workLocationAddress.address1,
                workLocationAddress.address2,
                workLocationAddress.city,
                workLocationAddress.province,
                workLocationAddress.zip,
                workLocationAddress.country,
              ]
                .filter(Boolean)
                .join(", ") || "N/A"}
            </Text>
          </Space>
        </div>

        <Divider style={{ margin: "8px 0" }} />

        <div style={{ marginTop: 8, textAlign: "right" }}>
          <Space>
            <Button
              danger
              disabled={isActionDisabled}
              onClick={() => handleCancelAppointment(appt)}
            >
              <CloseCircleOutlined /> <FormattedMessage id="Common.Cancel" />
            </Button>
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              disabled={isActionDisabled}
              onClick={() => handleReschedule(appt)}
            >
              <FormattedMessage id="Appointment.Reschedule" />
            </Button>
          </Space>
        </div>
      </div>
    );

    return {
      children: (
        <Popover content={content}>
          <div
            style={{
              backgroundColor: statusColors[appt.appointmentStatusId] || "#888",
              borderRadius: 0,
              padding: "3px",
              textAlign: "center",
              fontWeight: 500,
              color: "#fff",
              height: "48px",
              lineHeight: "38px",
            }}
          >
            <FormattedMessage id="Dispatch.Appointment" /> -{" "}
            {appt.appointmentNumber}
          </div>
        </Popover>
      ),
      props: { colSpan },
    };
  };

  const columns = [
    {
      title: (
        <FormattedMessage id="AddAppointment.Technicians/SubContractors" />
      ),
      dataIndex: "tech",
      key: "tech",
      fixed: "left",
      width: 180,
      render: (name, row) => {
        const initials = name
          ?.split(" ")
          .map((word) => word[0])
          .join("")
          .toUpperCase();

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Avatar
              style={{
                backgroundColor: "#f8f9fa",
                border: "solid 1px #eee",
                color: "#333",
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <div>
              <Text
                strong
                style={{ display: "block", width: "100%", lineHeight: "14px" }}
              >
                {name}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.type === "technician" ? (
                  <FormattedMessage id="Appointment.Technician" />
                ) : (
                  "Sub Contractor"
                )}
              </Text>
            </div>
          </div>
        );
      },
    },
    ...timeSlots.map((time) => ({
      title: time,
      dataIndex: time,
      key: time,
      width: 180,
      render: (_, row) => getAppointmentSpan(row, time),
    })),
  ];

  const dataSource = [
    ...(technicians?.map((tech) => ({
      key: `tech-${tech.id}`,
      tech: `${tech.firstName} ${tech.lastName || ""}`.trim(),
      id: tech.id,
      type: "technician",
    })) || []),

    ...(subContractorsList
      ?.filter((sub) => sub.isActive)
      .map((sub) => ({
        key: `sub-${sub.id}`,
        tech: `${sub.companyName} ${sub.lastName || ""}`.trim(),
        id: sub.id,
        type: "subcontractor",
      })) || []),
  ];

  return (
    <div className="medispamain time-calendar" style={{ position: "relative" }}>
      <div className="main-area">
        <div className="title-part">
          <Row align="middle" justify="space-between">
            <Col>
              <h4>
                <FormattedMessage id="Appointment.Dispatch" />
              </h4>
              <p className="mt-5px">
                <FormattedMessage id="Appointment.DispatchMessage" />
              </p>
            </Col>
          </Row>
        </div>
        <Card>
          <Row
            justify="end"
            align="middle"
            gutter={[16, 16]}
            style={{
              flexWrap: "wrap",
            }}
          >
            <Col xs={24} sm={24} md={18} className="text-right">
              <ul
                className="statslist-new"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px 16px",
                  padding: 0,
                  margin: 0,
                  justifyContent: "end",
                }}
              >
                <li
                  style={{
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      backgroundColor: "#a7a7a7",
                      borderRadius: "50%",
                      marginRight: 6,
                    }}
                  ></span>
                  <FormattedMessage id="Appointment.NotStarted" /> -{" "}
                  {appointmentCount.notStarted}
                </li>

                <li
                  style={{
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      backgroundColor: "#f57f01",
                      borderRadius: "50%",
                      marginRight: 6,
                    }}
                  ></span>
                  <FormattedMessage id="Appointment.InProgress" /> -{" "}
                  {appointmentCount.inProgress}
                </li>

                <li
                  style={{
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      backgroundColor: "#3cb043",
                      borderRadius: "50%",
                      marginRight: 6,
                    }}
                  ></span>
                  <FormattedMessage id="Appointment.Completed" /> -{" "}
                  {appointmentCount.completed}
                </li>
              </ul>
            </Col>

            <Col xs={24} sm={24} md={6}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <DatePicker
                  value={date}
                  onChange={(value) => value && setDate(value)}
                  style={{ width: "100%", maxWidth: 220 }}
                />
              </div>
            </Col>
          </Row>

          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            className="schedule mt-10"
            bordered
            scroll={{ x: 1000 }}
          />
        </Card>
        {selectClientModalOpen && (
          <SelectClient
            open={selectClientModalOpen}
            onCancel={() => setSelectClientModalOpen(false)}
            techId={selectedTechId}
            subContractorId={selectedSubContractorId}
            time={selectedTime}
            selectedDate={date}
            initialValues={modalInitialValues}
            mode={modalMode}
            resourceType={selectedResourceType}
          />
        )}
        <Modal
          title={<FormattedMessage id="AddAppointment.RescheduleAppointment" />}
          open={isModalOpen}
          onCancel={handleCancel}
          width={900}
          footer={null}
          centered
        >
          <DispatchTool
            onClose={handleCancel}
            mode={modalMode}
            initialValues={editAppointment ? appointmentByApptId : null}
            workOrderId={editAppointment?.workOrderId}
            isEditMode={!!editAppointment}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Schedule;
